import {
  imageSize,
  imageUrl,
  type MediaContext,
  type MediaFit,
} from "@/features/media/mediaUrl";
import type { Item, ProjectConfigs } from "@/types";

/**
 * How a POI is presented in the result list.
 *
 * `CardView` and `ListView` carried identical copies of this — including the
 * logo fallback and the path handling — so it lives once here and both render
 * from it.
 */
export interface ItemDisplay {
  title: string;
  subtitle: string;
  /** Null when the POI has no image and the tenant configured no logo either. */
  imageUrl: string | null;
}

/** Render a property value as text; anything non-scalar is skipped. */
function asText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.toString();
  }
  return "";
}

/**
 * The size to request for a POI thumbnail.
 *
 * Both views ask for 256×256, and only `contain` changes the resize mode — the
 * other `object-fit` values the config allows describe how the *element* lays
 * the image out, not how it should be cut.
 */
export function thumbnailSize(fit: string | undefined): string {
  return imageSize(256, 256, resizeMode(fit));
}

/**
 * The size to request for the image at the top of the POI details.
 *
 * Legacy asked for `800x600_${fit}`, so anything but `contain` produced a suffix
 * the resize endpoint doesn't define (`800x600_cover`). Only `_inside` exists.
 */
export function mainImageSize(fit: string | undefined): string {
  return imageSize(800, 600, resizeMode(fit));
}

/** Only `contain` changes how the image is *resized*; see `thumbnailSize`. */
function resizeMode(fit: string | undefined): MediaFit {
  return fit === "contain" ? "contain" : "cover";
}

/**
 * The POI's own image at `size`, falling back to the tenant's first logo.
 *
 * The fallback keeps the result list on an even rhythm instead of collapsing
 * image-less rows to text, and gives the details panel a branded header rather
 * than a blank band.
 */
export function itemImageUrl(
  item: Item,
  configs: ProjectConfigs,
  media: MediaContext,
  size: string
): string | null {
  const main = item.properties?.mediasets?.main ?? undefined;
  return (
    imageUrl(main, size, media) ??
    imageUrl(configs.appLogos?.[0]?.image, size, media)
  );
}

/** The Tailwind `object-fit` class for the configured `poiMainImageFit`. */
export function imageFitClass(fit: string | undefined): string {
  switch (fit) {
    case "contain":
      return "object-contain";
    case "cover":
      return "object-cover";
    case "fill":
      return "object-fill";
    case "none":
      return "object-none";
    default:
      return "object-scale-down";
  }
}

export function itemDisplay(
  item: Item,
  configs: ProjectConfigs,
  media: MediaContext
): ItemDisplay {
  const properties = item.properties ?? {};

  const title = configs.poiTitleField
    ? asText(properties[configs.poiTitleField])
    : "";

  const subtitle = (configs.poiSubtitleFields ?? [])
    .map((field) =>
      field.fieldName ? asText(properties[field.fieldName]) : ""
    )
    // Legacy joined every configured field unconditionally, so a POI missing one
    // rendered a stray ", " — or a leading one when the first was absent.
    .filter(Boolean)
    .join(", ");

  return {
    title,
    subtitle,
    imageUrl: itemImageUrl(
      item,
      configs,
      media,
      thumbnailSize(configs.poiMainImageFit)
    ),
  };
}

export type { MediaFit };
